import React, { useState, useEffect, useRef } from 'react';
import { FaImage, FaChevronRight, FaTh, FaStream, FaArrowsAltH, FaInfoCircle, FaStar, FaFilePdf, FaPlayCircle, FaTrashAlt, FaTimes, FaChevronLeft } from 'react-icons/fa';
import PhotoFeed from './PhotoFeed';
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { toast } from 'react-toastify';
import Lightbox from "yet-another-react-lightbox";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

const TabPhotos = ({ loadingPhotos, projectPhotos, user }) => {
    const [sliderValue, setSliderValue] = useState(50);
    const [viewMode, setViewMode] = useState('feed');
    const [activeFilter, setActiveFilter] = useState('Tout');
    const [isDiaporamaActive, setIsDiaporamaActive] = useState(false);
    const [favorites, setFavorites] = useState(() => {
        if (!user?.id) return [];
        const stored = localStorage.getItem(`fav_photos_${user.id}`);
        return stored ? JSON.parse(stored) : [];
    });

    // Story Viewer State
    const [activeStoryIndex, setActiveStoryIndex] = useState(null);
    const [storyProgress, setStoryProgress] = useState(0);
    const storyTimerRef = useRef(null);
    const STORY_DURATION = 5000; // 5 seconds per story

    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [isAutoplay, setIsAutoplay] = useState(false);

    const uniqueChantierNames = Array.from(new Set(projectPhotos.map(p => p.expand?.chantier?.name))).filter(Boolean);
    const filters = ['Tout', ...uniqueChantierNames];

    const filteredPhotos = activeFilter === 'Tout'
        ? projectPhotos
        : projectPhotos.filter(p => p.expand?.chantier?.name === activeFilter);

    const stories = filteredPhotos.slice(0, 10);

    const toggleFavorite = (photoId) => {
        setFavorites(prev => {
            const next = prev.includes(photoId)
                ? prev.filter(id => id !== photoId)
                : [...prev, photoId];
            localStorage.setItem(`fav_photos_${user.id}`, JSON.stringify(next));
            return next;
        });
    };

    // --- STORY VIEWER LOGIC ---
    useEffect(() => {
        if (activeStoryIndex !== null) {
            setStoryProgress(0);
            const interval = 50; // Update progress every 50ms
            const step = (interval / STORY_DURATION) * 100;

            storyTimerRef.current = setInterval(() => {
                setStoryProgress(prev => {
                    if (prev >= 100) {
                        handleNextStory();
                        return 0;
                    }
                    return prev + step;
                });
            }, interval);
        } else {
            clearInterval(storyTimerRef.current);
        }

        return () => clearInterval(storyTimerRef.current);
    }, [activeStoryIndex]);

    const handleNextStory = () => {
        if (activeStoryIndex < stories.length - 1) {
            setActiveStoryIndex(prev => prev + 1);
        } else {
            setActiveStoryIndex(null); // Close viewer at end
        }
    };

    const handlePrevStory = () => {
        if (activeStoryIndex > 0) {
            setActiveStoryIndex(prev => prev - 1);
        }
    };

    const generatePDFReport = async () => {
        try {
            if (!user) {
                toast.error("Profil utilisateur non chargé");
                return;
            }

            if (!projectPhotos || projectPhotos.length === 0) {
                toast.info("Aucune photo disponible pour le rapport.");
                return;
            }

            toast.info("Génération du rapport Premium en cours...");

            const doc = new jsPDF();
            const primaryColor = [164, 198, 57]; // #a4c639
            const darkColor = [30, 41, 59]; // #1e293b

            // --- HEADER DESIGN ---
            // Logo Shape (Stylized House for Maison Quinard)
            doc.setFillColor(...primaryColor);
            doc.rect(20, 15, 12, 12, 'F');
            doc.setFillColor(255, 255, 255);
            doc.triangle(21, 23, 26, 17, 31, 23, 'F'); // Little roof

            doc.setFont("helvetica", "bold");
            doc.setFontSize(24);
            doc.setTextColor(...darkColor);
            doc.text("MAISON QUINARD", 35, 25);

            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text("CONSTRUCTION BOIS & RÉNOVATION DURABLE", 35, 30);

            // Line separator
            doc.setDrawColor(...primaryColor);
            doc.setLineWidth(1.5);
            doc.line(20, 38, 190, 38);

            // --- PROJECT INFO BLOCK ---
            const project = projectPhotos[0]?.expand?.chantier || { name: "Chantier en cours", address: "Localisation à définir" };

            // Helper to clean characters not supported by standard PDF fonts (emojis, etc.)
            const cleanText = (str) => {
                if (!str) return "";
                // Remove emojis and special Unicode symbols that standard PDF fonts can't render
                return str.replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu, '')
                    .replace(/[^\x00-\xFF]/g, ''); // Strip any non-Latin1
            };

            doc.setFontSize(11);
            doc.setTextColor(...darkColor);
            doc.setFont("helvetica", "bold");
            doc.text("RAPPORT D'ACTIVITÉ TECHNIQUE", 20, 48);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.text(`Projet : ${cleanText(project.name)}`, 20, 54);
            doc.text(`Localisation : ${cleanText(project.address)}`, 20, 59);
            doc.text(`Client : ${cleanText(user.name || user.username)}`, 140, 54);
            doc.text(`Édition : ${new Date().toLocaleDateString('fr-FR')}`, 140, 59);

            let yPos = 75;

            // Helper to convert image URL to base64
            const getBase64Image = (url) => {
                return new Promise((resolve, reject) => {
                    const img = new Image();
                    img.setAttribute('crossOrigin', 'anonymous');
                    img.onload = () => {
                        const canvas = document.createElement("canvas");
                        canvas.width = img.width;
                        canvas.height = img.height;
                        const ctx = canvas.getContext("2d");
                        ctx.drawImage(img, 0, 0);
                        resolve(canvas.toDataURL("image/jpeg", 0.7));
                    };
                    img.onerror = reject;
                    img.src = url;
                });
            };

            for (let i = 0; i < Math.min(projectPhotos.length, 12); i++) {
                const photo = projectPhotos[i];

                if (yPos > 240) {
                    doc.addPage();
                    yPos = 30;
                }

                // Node background (Subtle card effect)
                doc.setDrawColor(240);
                doc.setLineWidth(0.1);
                doc.rect(18, yPos - 8, 174, 100, 'S');

                // Title Area
                doc.setFillColor(250, 250, 250);
                doc.rect(20, yPos - 6, 170, 25, 'F');

                doc.setFont("helvetica", "bold");
                doc.setFontSize(12);
                doc.setTextColor(...primaryColor);
                doc.text(`MAINTENANCE / ÉTAPE #${projectPhotos.length - i}`, 25, yPos);

                doc.setFont("helvetica", "normal");
                doc.setFontSize(9);
                doc.setTextColor(100);
                const dateStr = new Date(photo.created).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
                const workerName = photo.expand?.worker?.name || 'Artisan Maison Quinard';

                // Weather & Location Tag
                const weatherStr = photo.weather ? `| Météo : ${photo.weather}` : "";
                const locStr = photo.location ? `| GPS : ${photo.location}` : "";
                doc.text(`Posté le ${dateStr} par ${workerName} ${weatherStr} ${locStr}`, 25, yPos + 6);

                doc.setFontSize(10);
                doc.setTextColor(...darkColor);
                const note = cleanText(photo.caption || "Inspection visuelle et avancement des travaux.");
                doc.text(`Observation : ${note.substring(0, 90)}${note.length > 90 ? '...' : ''}`, 25, yPos + 13);

                try {
                    const imgData = await getBase64Image(photo.url);
                    // Add image with border
                    doc.setDrawColor(...primaryColor);
                    doc.setLineWidth(0.5);
                    doc.addImage(imgData, 'JPEG', 60, yPos + 18, 90, 65);
                    doc.rect(60, yPos + 18, 90, 65, 'S');
                    yPos += 105;
                } catch (e) {
                    doc.setTextColor(150);
                    doc.text("[Aperçu non disponible - Données sécurisées]", 60, yPos + 40);
                    yPos += 55;
                }
            }

            // --- FOOTER / SIGNATURE AREA ---
            if (yPos > 240) {
                doc.addPage();
                yPos = 30;
            }

            doc.setDrawColor(200);
            doc.line(20, yPos, 190, yPos);
            yPos += 15;

            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.text("SIGNATURE DU CHEF DE PROJET", 20, yPos);
            doc.text("CERTIFICATION PRODUIT", 140, yPos);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.text("Document certifié par Maison Quinard ERP v2.0", 20, yPos + 5);
            doc.text("Conformité RE2020 / Qualibat RGE", 140, yPos + 5);

            // Stylized Signature area
            doc.setDrawColor(240);
            doc.rect(20, yPos + 8, 50, 20, 'S');

            doc.save(`Rapport_Technique_${project.name.replace(/\s+/g, '_')}_${new Date().getTime()}.pdf`);
            toast.success("Rapport professionnel généré !");
        } catch (error) {
            console.error("PDF Error:", error);
            toast.error("Erreur technique lors du rendu PDF.");
        }
    };

    const currentViewPhotos = viewMode === 'favorites' ? projectPhotos.filter(p => favorites.includes(p.id)) : filteredPhotos;
    const slides = currentViewPhotos.map(p => ({
        src: p.url,
        title: p.caption,
        description: `Mise à jour du ${new Date(p.created).toLocaleDateString()}`
    }));

    const favoritePhotos = projectPhotos.filter(p => favorites.includes(p.id));

    return (
        <div className="photos-page scale-in">
            {/* Header */}
            <div className="w-page-header" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ marginBottom: '20px' }}>
                    <h2 className="w-page-title">Journal Photo</h2>
                    <p className="w-page-subtitle">Suivez l'évolution de votre construction</p>
                </div>
            </div>

            {/* View Switcher */}
            <div style={{ padding: '0 20px 15px' }}>
                <div className="w-view-switch">
                    <button className={viewMode === 'feed' ? 'active' : ''} onClick={() => setViewMode('feed')}><FaStream /> Fil</button>
                    <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}><FaTh /> Galerie</button>
                    <button className={viewMode === 'favorites' ? 'active' : ''} onClick={() => setViewMode('favorites')}>
                        <FaStar style={{ color: viewMode === 'favorites' ? '#fbbf24' : 'inherit' }} /> Pépites {favorites.length > 0 && `(${favorites.length})`}
                    </button>
                </div>
            </div>

            {/* Quick Actions */}
            {viewMode !== 'favorites' && (
                <div style={{ padding: '0 20px 20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button className="w-btn-full" onClick={() => setIsDiaporamaActive(true)} style={{ flex: '1 1 0', background: '#3b82f6' }}>
                        <FaPlayCircle /> Diaporama
                    </button>
                    <button className="w-btn-full" onClick={generatePDFReport} style={{ flex: '1 1 0', background: 'var(--color-primary)' }}>
                        <FaFilePdf /> Rapport PDF
                    </button>
                </div>
            )}

            {/* Stories Section */}
            {stories.length > 0 && viewMode !== 'favorites' && (
                <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
                    <div className="stories-container" style={{ width: '100%', boxSizing: 'border-box' }}>
                        {stories.map((story, idx) => (
                            <div key={story.id} className="story-node" onClick={() => setActiveStoryIndex(idx)}>
                                <div className="story-circle">
                                    <div className="story-inner">
                                        <img src={story.thumb || story.url} alt="Story" />
                                    </div>
                                </div>
                                <span className="story-label">
                                    {idx === 0 ? 'Récent' : new Date(story.created).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Immersive Story Viewer Modal */}
            {activeStoryIndex !== null && (
                <div className="story-viewer-overlay">
                    <div className="story-viewer-content">
                        {/* Progress Bars */}
                        <div className="story-progress-bar-container">
                            {stories.map((_, idx) => (
                                <div key={idx} className="story-progress-bar-track">
                                    <div
                                        className="story-progress-bar-fill"
                                        style={{ width: idx < activeStoryIndex ? '100%' : (idx === activeStoryIndex ? `${storyProgress}%` : '0%') }}
                                    />
                                </div>
                            ))}
                        </div>

                        {/* Story Header */}
                        <div className="story-header">
                            <div className="story-user-info">
                                <div className="story-mini-avatar">MQ</div>
                                <div>
                                    <div className="story-user-name">
                                        {stories[activeStoryIndex].expand?.worker?.name || 'Maison Quinard'}
                                    </div>
                                    <div className="story-time">
                                        {new Date(stories[activeStoryIndex].created).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                </div>
                            </div>
                            <button className="story-close-btn" onClick={() => setActiveStoryIndex(null)}>
                                <FaTimes />
                            </button>
                        </div>

                        {/* Image Display */}
                        <div className="story-media-container">
                            <img src={stories[activeStoryIndex].url} alt="Story Content" />
                            <div className="story-caption">
                                {stories[activeStoryIndex].caption || 'Mise à jour du chantier'}
                            </div>
                        </div>

                        {/* Navigation Areas */}
                        <div className="story-nav-left" onClick={handlePrevStory} />
                        <div className="story-nav-right" onClick={handleNextStory} />
                    </div>
                </div>
            )}

            {/* Filter Chips */}
            {viewMode !== 'favorites' && filters.length > 2 && (
                <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
                    <div className="filter-scroller" style={{ width: '100%', boxSizing: 'border-box' }}>
                        {filters.map(filter => (
                            <button
                                key={filter}
                                className={`filter-chip ${activeFilter === filter ? 'active' : ''}`}
                                onClick={() => setActiveFilter(filter)}
                            >
                                {filter}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Content Rendering */}
            <div style={{ padding: '0 20px' }}>
                {viewMode === 'feed' ? (
                    <PhotoFeed
                        photos={filteredPhotos}
                        user={user}
                        favorites={favorites}
                        toggleFavorite={toggleFavorite}
                        isDiaporama={isDiaporamaActive}
                        closeDiaporama={() => setIsDiaporamaActive(false)}
                    />
                ) : viewMode === 'favorites' ? (
                    favoritePhotos.length === 0 ? (
                        <div className="w-empty-state">
                            <div className="empty-icon" style={{ background: '#fef3c7', color: '#fbbf24' }}><FaStar /></div>
                            <h3>Votre coffre-fort est vide</h3>
                            <p>Marquez vos photos préférées avec l'étoile pour les retrouver ici.</p>
                            <button className="w-btn-outline" onClick={() => setViewMode('feed')} style={{ margin: '15px auto 0' }}>Retourner au fil</button>
                        </div>
                    ) : (
                        <div className="masonry-grid">
                            {favoritePhotos.map((photo) => (
                                <div key={photo.id} className="masonry-item">
                                    <div className="photo-card" onClick={() => { setSelectedIndex(favoritePhotos.findIndex(p => p.id === photo.id)); setIsAutoplay(true); }}>
                                        <img src={photo.thumb} alt={photo.caption} />
                                        <button className="fav-delete-btn" onClick={(e) => { e.stopPropagation(); toggleFavorite(photo.id); }}>
                                            <FaTrashAlt />
                                        </button>
                                        <div className="photo-info">
                                            <p>{photo.caption || "Photo Favoris"}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                ) : (
                    <>
                        {/* Galerie Grid / Slider */}
                        {filteredPhotos.length >= 2 && activeFilter === 'Tout' && (
                            <div className="w-card premium" style={{ padding: 0, marginBottom: '20px', overflow: 'hidden' }}>
                                <div className="w-card-header" style={{ padding: '15px 20px' }}>
                                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 900, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <FaArrowsAltH style={{ color: 'var(--color-primary)' }} /> Évolution du Projet
                                    </h3>
                                </div>
                                <div className="slider-container">
                                    <div className="before-image">
                                        <img src={filteredPhotos[filteredPhotos.length - 1].url} alt="Avant" />
                                    </div>
                                    <div className="after-image" style={{ clipPath: `inset(0 0 0 ${sliderValue}%)` }}>
                                        <img src={filteredPhotos[0].url} alt="Après" />
                                    </div>
                                    <div className="slider-divider" style={{ left: `${sliderValue}%` }}></div>
                                    <div className="slider-handle" style={{ left: `${sliderValue}%` }}>
                                        <FaArrowsAltH />
                                    </div>
                                    <input type="range" min="0" max="100" value={sliderValue} onChange={(e) => setSliderValue(e.target.value)} className="comparison-slider" />
                                    <div className="slider-labels">
                                        <span>INITIAL</span>
                                        <span>ACTUEL</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="masonry-grid">
                            {filteredPhotos.map((photo, idx) => (
                                <div key={photo.id} className="masonry-item" onClick={() => { setSelectedIndex(idx); setIsAutoplay(true); }}>
                                    <div className="photo-card">
                                        <img src={photo.thumb} alt={photo.caption} />
                                        <div className="photo-info">
                                            <p>{photo.caption || "Mise à jour"}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* Global Lightbox */}
            <Lightbox
                index={selectedIndex}
                slides={slides}
                open={selectedIndex >= 0}
                close={() => { setSelectedIndex(-1); setIsAutoplay(false); }}
                plugins={[Fullscreen, Slideshow, Thumbnails, Zoom]}
                slideshow={{ autoplay: isAutoplay, delay: 4000 }}
            />

            {/* INLINE STYLES MOBILE-FIRST */}
            <style>{`
                .photos-page { min-height: 100vh; background: #f8fafc; margin-top: -20px; padding-bottom: 80px; overflow-x: hidden; }
                .photos-page * { box-sizing: border-box; }
                .w-page-header { padding: 30px 20px 15px; display: flex; justify-content: space-between; align-items: flex-end; flex-wrap: wrap; }
                .w-page-title { font-size: 22px; font-weight: 900; color: #0f172a; margin: 0; word-break: break-word; }
                .w-page-subtitle { font-size: 13px; color: #94a3b8; margin: 4px 0 0; }

                .w-view-switch { display: flex; background: #e2e8f0; padding: 4px; border-radius: 12px; flex-wrap: wrap; }
                .w-view-switch button { flex: 1 1 0; min-width: 0; padding: 8px 4px; border: none; background: transparent; border-radius: 8px; font-weight: 800; color: #64748b; font-size: 13px; display: flex; align-items: center; justify-content: center; gap: 4px; cursor: pointer; transition: 0.2s; overflow: hidden; text-overflow: ellipsis; word-break: break-word; }
                .w-view-switch button.active { background: #fff; color: var(--color-primary); box-shadow: 0 2px 5px rgba(0,0,0,0.05); }

                .w-btn-full { padding: 14px; border-radius: 12px; border: none; font-weight: 800; color: #fff; display: flex; justify-content: center; align-items: center; gap: 8px; cursor: pointer; transition: transform 0.1s; overflow: hidden; text-overflow: ellipsis; word-break: break-word; }
                .w-btn-full:active { transform: scale(0.98); }
                
                .w-empty-state { text-align: center; padding: 40px 20px; background: #fff; border-radius: 16px; border: 1px dashed #cbd5e1; }
                .empty-icon { width: 48px; height: 48px; border-radius: 16px; background: #f8fafc; display: flex; align-items: center; justify-content: center; margin: 0 auto 15px; font-size: 20px; color: #94a3b8; }
                .w-empty-state h3 { margin: 0 0 5px; font-size: 16px; color: #0f172a; }
                .w-empty-state p { margin: 0; font-size: 13px; color: #64748b; }
                .w-btn-outline { background: transparent; border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 12px; font-size: 12px; font-weight: 700; display: flex; align-items: center; gap: 6px; cursor: pointer; color: #475569; }

                .w-card { background: #fff; border-radius: 16px; border: 1px solid #f1f5f9; }

                /* STORIES */
                .stories-container { display: flex; justify-content: center; gap: 12px; overflow-x: auto; padding: 0 20px 20px; scrollbar-width: none; -webkit-overflow-scrolling: touch; }
                .stories-container::-webkit-scrollbar { display: none; }
                .story-node { display: flex; flex-direction: column; align-items: center; gap: 6px; cursor: pointer; flex-shrink: 0; width: 64px; }
                .story-circle { width: 64px; height: 64px; border-radius: 50%; padding: 3px; background: linear-gradient(45deg, var(--color-primary), #3b82f6); }
                .story-inner { width: 100%; height: 100%; border-radius: 50%; border: 2px solid #fff; overflow: hidden; background: #e2e8f0; }
                .story-inner img { width: 100%; height: 100%; object-fit: cover; }
                .story-label { font-size: 10px; font-weight: 700; color: #64748b; text-align: center; }

                /* STORY VIEWER */
                .story-viewer-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: #111; z-index: 9999; display: flex; align-items: center; justify-content: center; }
                .story-viewer-content { width: 100%; max-width: 500px; height: 100%; position: relative; background: #000; overflow: hidden; }
                .story-progress-bar-container { position: absolute; top: 15px; left: 10px; right: 10px; display: flex; gap: 5px; z-index: 10; }
                .story-progress-bar-track { flex: 1; height: 3px; background: rgba(255,255,255,0.3); border-radius: 3px; overflow: hidden; }
                .story-progress-bar-fill { height: 100%; background: #fff; border-radius: 3px; }
                .story-header { position: absolute; top: 30px; left: 15px; right: 15px; display: flex; justify-content: space-between; align-items: center; z-index: 10; }
                .story-user-info { display: flex; align-items: center; gap: 10px; color: #fff; }
                .story-mini-avatar { width: 32px; height: 32px; border-radius: 50%; background: rgba(255,255,255,0.2); display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: bold; border: 1px solid rgba(255,255,255,0.5); }
                .story-user-name { font-weight: 800; font-size: 13px; text-shadow: 0 1px 3px rgba(0,0,0,0.8); }
                .story-time { font-size: 11px; opacity: 0.8; text-shadow: 0 1px 3px rgba(0,0,0,0.8); }
                .story-close-btn { background: transparent; border: none; color: #fff; font-size: 24px; cursor: pointer; text-shadow: 0 1px 3px rgba(0,0,0,0.8); padding: 5px; }
                .story-media-container { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; position: relative; }
                .story-media-container img { width: 100%; height: 100%; object-fit: contain; }
                .story-caption { position: absolute; bottom: 40px; left: 20px; right: 20px; text-align: center; color: #fff; font-weight: 700; font-size: 14px; text-shadow: 0 1px 5px rgba(0,0,0,0.8); background: rgba(0,0,0,0.4); padding: 10px; border-radius: 12px; backdrop-filter: blur(5px); }
                .story-nav-left, .story-nav-right { position: absolute; top: 0; bottom: 0; width: 40%; z-index: 5; }
                .story-nav-left { left: 0; }
                .story-nav-right { right: 0; }

                /* FILTER CHIPS */
                .filter-scroller { display: flex; gap: 8px; overflow-x: auto; padding: 0 20px 15px; margin-bottom: 10px; scrollbar-width: none; -webkit-overflow-scrolling: touch; }
                .filter-scroller::-webkit-scrollbar { display: none; }
                .filter-chip { flex-shrink: 0; padding: 8px 16px; border-radius: 20px; font-size: 12px; font-weight: 800; border: 1px solid #e2e8f0; background: #fff; color: #64748b; white-space: nowrap; cursor: pointer; }
                .filter-chip.active { background: #0f172a; color: #fff; border-color: #0f172a; }

                /* MASONRY GRID */
                .masonry-grid { column-count: 2; column-gap: 10px; }
                .masonry-item { break-inside: avoid; margin-bottom: 10px; }
                .photo-card { border-radius: 16px; overflow: hidden; background: #fff; position: relative; cursor: pointer; box-shadow: 0 2px 5px rgba(0,0,0,0.02); }
                .photo-card img { width: 100%; display: block; }
                .photo-info { padding: 12px; }
                .photo-info p { margin: 0; font-size: 11px; font-weight: 800; color: #334155; }
                .fav-delete-btn { position: absolute; top: 8px; right: 8px; background: rgba(255,255,255,0.9); border: none; border-radius: 50%; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #ef4444; box-shadow: 0 2px 5px rgba(0,0,0,0.1); }

                /* SLIDER */
                .slider-container { position: relative; width: 100%; aspect-ratio: 16/9; background: #e2e8f0; }
                .before-image, .after-image { position: absolute; top: 0; left: 0; width: 100%; height: 100%; overflow: hidden; }
                .before-image img, .after-image img { width: 100%; height: 100%; object-fit: cover; }
                .slider-divider { position: absolute; top: 0; bottom: 0; width: 4px; background: #fff; transform: translateX(-50%); box-shadow: 0 0 10px rgba(0,0,0,0.5); pointer-events: none; }
                .slider-handle { position: absolute; top: 50%; width: 40px; height: 40px; background: #fff; border-radius: 50%; display: flex; align-items: center; justify-content: center; transform: translate(-50%, -50%); box-shadow: 0 2px 10px rgba(0,0,0,0.3); color: var(--color-primary); font-size: 18px; pointer-events: none; }
                .comparison-slider { position: absolute; top: 0; left: 0; width: 100%; height: 100%; opacity: 0; cursor: pointer; }
                .slider-labels { position: absolute; bottom: 10px; left: 15px; right: 15px; display: flex; justify-content: space-between; pointer-events: none; }
                .slider-labels span { background: rgba(0,0,0,0.6); color: #fff; padding: 4px 8px; border-radius: 6px; font-size: 9px; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; backdrop-filter: blur(4px); }

                /* DARK MODE OVERRIDES */
                [data-theme="dark"] .photos-page { background: var(--color-background, #121212); }
                [data-theme="dark"] .w-page-title { color: #ffffff; }
                [data-theme="dark"] .w-page-subtitle { color: #a1a1aa; }
                [data-theme="dark"] .w-view-switch { background: #27272a; }
                [data-theme="dark"] .w-view-switch button.active { background: #18181b; }
                [data-theme="dark"] .w-card { background: #1e1e1e; border-color: rgba(255,255,255,0.08); }
                [data-theme="dark"] .w-card-header h3 { color: #ffffff !important; }
                [data-theme="dark"] .w-empty-state { background: #1e1e1e; border-color: rgba(255,255,255,0.1); }
                [data-theme="dark"] .w-empty-state h3 { color: #e4e4e7; }
                [data-theme="dark"] .empty-icon { background: #27272a; }
                [data-theme="dark"] .w-btn-outline { border-color: rgba(255,255,255,0.1); color: #e4e4e7; }
                [data-theme="dark"] .story-inner { border-color: #121212; }
                [data-theme="dark"] .filter-chip { background: #1e1e1e; border-color: #3f3f46; color: #a1a1aa; }
                [data-theme="dark"] .filter-chip.active { background: #e4e4e7; color: #18181b; border-color: #e4e4e7; }
                [data-theme="dark"] .photo-card { background: #1e1e1e; }
                [data-theme="dark"] .photo-info p { color: #e4e4e7; }
                [data-theme="dark"] .fav-delete-btn { background: rgba(0,0,0,0.6); color: #ef4444; }
            `}</style>
        </div>
    );
};

export default TabPhotos;
